import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import { Skeleton } from '@mui/material';
import { Checklist } from '@client/new/pages/creator/guide/model';
import { ChecklistProgress } from '@client/new/pages/creator/guide/components/ChecklistProgress';
import { ChecklistTaskItem } from '@client/new/pages/creator/guide/components/ChecklistTaskItem';
import * as styles from './GuideChecklistPreview.css';

type GuideChecklistPreviewProps = {
  checklist: Checklist;
  checklistNumber: number;
  influencerId: number;
  hasStrategy: boolean;
  onTaskToggle: (taskId: string, completed: boolean) => void;
  onOpenGuide: () => void;
  onStartStrategy: () => void;
  onOpenTaskModal: (templateId: string) => void;
};

const MAX_VISIBLE_TASKS = 3;

export const GuideChecklistPreviewSkeleton = () => (
  <div className={styles.container}>
    <div className={styles.checklistSection}>
      <div className={styles.checklistHeader}>
        <div className={styles.checklistHeaderContent}>
          <Skeleton variant="text" width={100} height={16} />
          <Skeleton variant="text" width={220} height={24} />
        </div>
        <Skeleton variant="text" width={80} height={16} />
      </div>
      <div className={styles.progressSection}>
        <Skeleton variant="rounded" width="100%" height={8} />
      </div>
      <div className={styles.tasksContainer}>
        {[0, 1, 2].map((i) => (
          <div key={i} className={styles.taskWrapper}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px',
              }}
            >
              <div style={{ flex: 1 }}>
                <Skeleton
                  variant="text"
                  width={`${60 + i * 10}%`}
                  height={20}
                />
                <Skeleton variant="text" width={`${40 + i * 5}%`} height={14} />
              </div>
              <Skeleton variant="circular" width={20} height={20} />
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const GuideChecklistPreview = ({
  checklist,
  checklistNumber,
  influencerId,
  hasStrategy,
  onTaskToggle,
  onOpenGuide,
  onStartStrategy,
  onOpenTaskModal,
}: GuideChecklistPreviewProps) => {
  const completedTasks = checklist.tasks.filter((t) => t.isChecked).length;
  const totalTasks = checklist.tasks.length;
  const showAllTasks = !hasStrategy;
  const visibleTasks = showAllTasks
    ? checklist.tasks
    : checklist.tasks.slice(0, MAX_VISIBLE_TASKS);

  return (
    <div className={hasStrategy ? styles.container : styles.containerNoBorder}>
      <div className={styles.checklistSection}>
        <div className={styles.checklistHeader}>
          <div className={styles.checklistHeaderContent}>
            {hasStrategy && (
              <div className={styles.checklistLabel}>
                Checklist #{checklistNumber}
              </div>
            )}
            <h3 className={styles.checklistTitle}>{checklist.title}</h3>
          </div>
          {checklist.estimatedDurationLabel && (
            <span className={styles.estimatedTime}>
              <FontAwesomeIcon icon={faClock} className={styles.clockIcon} />
              {checklist.estimatedDurationLabel}
            </span>
          )}
        </div>

        <div className={styles.progressSection}>
          <ChecklistProgress completed={completedTasks} total={totalTasks} />
        </div>

        <div className={styles.tasksWrapper}>
          <div className={styles.tasksContainer}>
            {visibleTasks.map((task) => (
              <div key={task.id} className={styles.taskWrapper}>
                <ChecklistTaskItem
                  task={task}
                  onToggle={onTaskToggle}
                  onOpenModal={onOpenTaskModal}
                  influencerId={influencerId}
                />
              </div>
            ))}
          </div>
          {hasStrategy && <div className={styles.gradientOverlay} />}
        </div>
      </div>

      {hasStrategy && (
        <div className={styles.ctaSection}>
          <span className={styles.ctaText}>To view all steps</span>
          <button
            type="button"
            className={styles.ctaButton}
            onClick={onOpenGuide}
          >
            Open strategy guide
            <FontAwesomeIcon icon={faArrowRight} />
          </button>
        </div>
      )}
      {!hasStrategy && (
        <div className={styles.ctaSection}>
          <button
            type="button"
            className={styles.ctaButton}
            onClick={onStartStrategy}
          >
            Start with a Strategy
            <FontAwesomeIcon icon={faArrowRight} />
          </button>
        </div>
      )}
    </div>
  );
};
